import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { urlParam } from '../lib/deepLink';
import { PROFIT_PATH, profitKey, usePageTabs } from '../lib/pageTabs';
import { usePageActive } from '../lib/pageActive';
import { api, type ProfitLine, type ProfitAutoVal, type ProfitLive, type ProfitManualItem, type ProfitMonthResult, type ProfitMonthRow } from '../lib/api';
import { useSticky } from '../lib/sticky';
import { ErrorBox, money, MoneyInput } from '../components/DataTable';
import Modal from '../components/Modal';
import { canScreen, useMe } from '../lib/me';
import ProfitCust from './ProfitCust';
import ProfitCustTrend from './ProfitCustTrend';
import ProfitMisu from './ProfitMisu';
import ProfitIdle from './ProfitIdle';
import ProfitRoute from './ProfitRoute';
import ProfitInst from './ProfitInst';
import ProfitDirect from './ProfitDirect';
import ProfitRepair from './ProfitRepair';
import ProfitMinus from './ProfitMinus';

/**
 * 경영 분석 — 추세(10-24 way · 구글시트 `주요정산계정추세분석_YYYY` 이관). 서버 `functions/src/profit.ts`.
 * 시트 탭과 같은 자리: 수익추세(월별 지표) · ANA(누계·표준월·연말 예상) · 년도분석(올해·작년·2년 전) · 목표영업매출.
 * 값은 굳힌 월 기록뿐이다 — 2019-01~2026-08 은 연도별 시트에서 옮겼고(시트), 그 뒤는 웹에서 굳힌다.
 */

type Metric = 'rev' | 'exp' | 'profit' | 'real_sales' | 'commute_sales' | 'general_sales' | 'sales'
  | 'commute_profit' | 'general_profit' | 'personal' | 'fin_add' | 'direct_cost';
const LABEL: Record<Metric, string> = {
  rev: '총수입', exp: '총지출', profit: '총수익', real_sales: '실매출', commute_sales: '통근매출', general_sales: '일반매출',
  sales: '영업매출', commute_profit: '통근수익', general_profit: '일반수익', personal: '개인오더', fin_add: '재무추가지급공제', direct_cost: '직영비용',
};
const TIP: Partial<Record<Metric, string>> = {
  rev: '통근 + 일반 + 개인 + 공제금 + 유동자금(상계)', real_sales: '통근 + 일반 + 개인 + 공제금', sales: '통근매출 + 일반매출',
  direct_cost: '할부·급여 제외 · 2024-10 부터', fin_add: '재무추가 공제 − 지급',
};
const TABLE_ROWS: Metric[] = ['rev', 'exp', 'real_sales', 'profit', 'commute_sales', 'general_sales', 'sales', 'commute_profit', 'general_profit', 'personal', 'fin_add', 'direct_cost'];
const REVIEW_ROWS: Metric[] = ['rev', 'exp', 'profit', 'commute_sales', 'general_sales', 'sales'];
const COMPARE: Metric[] = ['sales', 'profit', 'rev', 'commute_sales', 'general_sales', 'commute_profit', 'general_profit'];
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const num = (v: string | null | undefined) => (v === null || v === undefined || v === '' ? null : Number(v));
const key = (y: number, m: number) => `${y}-${String(m).padStart(2, '0')}`;
const pct = (v: number | null) => (v === null || !Number.isFinite(v) ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%`);
const tone = (v: number | null) => (v === null ? 'text-ink-faint' : v < 0 ? 'text-red-700' : 'text-green-700');
const eok = (v: number) => (Math.abs(v) >= 1e8 ? `${(v / 1e8).toFixed(1)}억` : `${Math.round(v / 1e4).toLocaleString('ko-KR')}만`);

/** 지표 값 — 영업매출은 통근 + 일반(둘 중 하나라도 없으면 없음). */
function val(r: ProfitMonthRow | undefined, m: Metric): number | null {
  if (!r) return null;
  if (m === 'sales') {
    const a = num(r.commute_sales), b = num(r.general_sales);
    return a === null || b === null ? null : a + b;
  }
  return num(r[m]);
}

function TrendView() {
  const { me } = useMe();
  const canWrite = canScreen(me, 'profit', 'write');
  const [rows, setRows] = useState<ProfitMonthRow[]>([]);
  const [targets, setTargets] = useState<{ id: number; yr: number; amt: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const [year, setYear] = useState<number | null>(null);
  const [cmp, setCmp] = useState<Metric>('sales');
  const [showYoy, setShowYoy] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.listProfitTrend({}).then((r) => {
      setRows(r.rows); setTargets(r.targets);
      setYear((y) => y ?? (r.rows.length ? Number(r.rows.at(-1)!.mon.slice(0, 4)) : new Date().getFullYear()));
    }).catch((e) => setError(errMsg(e))).finally(() => setLoading(false));
  }, [nonce]);

  const byMon = useMemo(() => new Map(rows.map((r) => [r.mon, r])), [rows]);
  const years = useMemo(() => [...new Set(rows.map((r) => Number(r.mon.slice(0, 4))))].sort((a, b) => b - a), [rows]);
  if (error) return <ErrorBox message={error} />;
  if (loading && !rows.length) return <div className="py-10 text-center text-sm text-ink-soft">불러오는 중…</div>;
  if (!year) return <div className="card p-6 text-sm text-ink-soft">아직 기록이 없습니다.</div>;

  // 그 해 기록이 있는 달 수(n) — 시트 `COUNT(수익추세!D2:O2)` 와 같은 뜻.
  const filled = MONTHS.filter((m) => byMon.has(key(year, m)) && val(byMon.get(key(year, m)), 'rev') !== null);
  const n = filled.length;
  const sumOf = (y: number, m: Metric, months: number[]) => {
    const vs = months.map((mm) => val(byMon.get(key(y, mm)), m));
    return vs.some((v) => v !== null) ? vs.reduce<number>((a, v) => a + (v ?? 0), 0) : null;
  };
  const last = filled.at(-1);
  const src = filled.map((m) => byMon.get(key(year, m))!.source);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select className="field w-auto" value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label="연도">
          {years.map((y) => <option key={y} value={y}>{y}년</option>)}
        </select>
        <span className="text-xs text-ink-soft">
          {n ? `${n}개월 기록(${last}월까지)` : '기록 없음'}
          {src.includes('sheet') && ' · 시트에서 옮긴 값'}{src.includes('web') && ' · 웹에서 굳힌 값'}
        </span>
      </div>

      <section className="card p-4">
        <h2 className="mb-2 text-sm font-semibold">{year}년 Review</h2>
        {n === 0 ? <div className="text-xs text-ink-faint">이 해 기록이 없습니다.</div> : (
          <div className="overflow-x-auto">
            <table className="min-w-[36rem] text-sm">
              <thead><tr className="text-xs text-ink-soft">
                <th className="th text-left">구분</th><th className="th text-right">~{last}월 누계</th>
                <th className="th text-right">표준월(1~{last}월 평균)</th><th className="th text-right">예상(12월 말)</th>
              </tr></thead>
              <tbody>
                {REVIEW_ROWS.map((m) => {
                  const cum = sumOf(year, m, filled) ?? 0;
                  const avg = cum / n;
                  const est = cum + avg * (12 - n);
                  const prevCum = sumOf(year - 1, m, filled);
                  const prevAll = sumOf(year - 1, m, MONTHS);
                  const yoy = (cur: number, prev: number | null) => (prev ? (cur - prev) / Math.abs(prev) : null);
                  const revCum = sumOf(year, 'rev', filled) ?? 0;
                  return (
                    <tr key={m} className="border-t border-line">
                      <td className="td font-medium" title={TIP[m]}>{LABEL[m]}</td>
                      <td className="td text-right tabular-nums">{money(Math.round(cum))}<div className={`text-xs ${tone(yoy(cum, prevCum))}`}>작년대비 {pct(yoy(cum, prevCum))}</div></td>
                      <td className="td text-right tabular-nums">{money(Math.round(avg))}</td>
                      <td className="td text-right tabular-nums">{money(Math.round(est))}<div className={`text-xs ${tone(yoy(est, prevAll))}`}>작년 연간대비 {pct(yoy(est, prevAll))}</div>
                        {m === 'profit' && revCum ? <div className="text-xs text-ink-soft">수익률 {(cum / revCum * 100).toFixed(1)}%</div> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card p-4">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold">연도 비교</h2>
          <select className="field w-auto" value={cmp} onChange={(e) => setCmp(e.target.value as Metric)} aria-label="지표">
            {COMPARE.map((m) => <option key={m} value={m}>{LABEL[m]}</option>)}
          </select>
        </div>
        <CompareChart year={year} metric={cmp} get={(y, m) => val(byMon.get(key(y, m)), cmp)} />
        <div className="mt-2 overflow-x-auto">
          <table className="min-w-[48rem] text-xs">
            <thead><tr className="text-ink-soft">
              <th className="th text-left">{LABEL[cmp]}</th>
              {MONTHS.map((m) => <th key={m} className="th text-right">{m}월</th>)}
              <th className="th text-right">합계</th>
            </tr></thead>
            <tbody>
              {[year, year - 1, year - 2].map((y) => (
                <tr key={y} className="border-t border-line">
                  <td className="td font-medium">{y}</td>
                  {MONTHS.map((m) => { const v = val(byMon.get(key(y, m)), cmp); return <td key={m} className="td text-right tabular-nums">{v === null ? '—' : eok(v)}</td>; })}
                  <td className="td text-right tabular-nums">{(() => { const s = sumOf(y, cmp, MONTHS); return s === null ? '—' : eok(s); })()}</td>
                </tr>
              ))}
              {[1, 2].map((d) => (
                <tr key={d} className="border-t border-line text-ink-soft">
                  <td className="td">{d === 1 ? '작년대비' : '2년 전 대비'}</td>
                  {MONTHS.map((m) => {
                    const a = val(byMon.get(key(year, m)), cmp), b = val(byMon.get(key(year - d, m)), cmp);
                    const r = a === null || !b ? null : (a - b) / Math.abs(b);
                    return <td key={m} className={`td text-right tabular-nums ${tone(r)}`}>{pct(r)}</td>;
                  })}
                  <td className="td" />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card p-4">
        <div className="mb-2 flex flex-wrap items-center gap-3">
          <h2 className="text-sm font-semibold">{year}년 월별 지표</h2>
          <label className="flex items-center gap-1 text-xs text-ink-soft">
            <input type="checkbox" checked={showYoy} onChange={(e) => setShowYoy(e.target.checked)} /> 작년대비 줄
          </label>
        </div>
        <div className="max-h-[calc(100vh-12rem)] overflow-auto">
          <table className="min-w-[64rem] text-xs [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:bg-surface">
            <thead><tr className="text-ink-soft">
              <th className="th text-left">구분</th><th className="th text-right">소계</th>
              {MONTHS.map((m) => <th key={m} className="th text-right">{m}월</th>)}
            </tr></thead>
            <tbody>
              {TABLE_ROWS.map((m) => (
                <MetricRows key={m} m={m} year={year} showYoy={showYoy} get={(y, mm) => val(byMon.get(key(y, mm)), m)} sum={sumOf(year, m, MONTHS)} />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Targets year={year} n={n} last={last ?? 0} cum={sumOf(year, 'sales', filled)} lastMonth={last ? val(byMon.get(key(year, last)), 'sales') : null}
               targets={targets.filter((t) => t.yr === year)} canWrite={canWrite} onChanged={() => setNonce((x) => x + 1)} />
    </div>
  );
}

function MetricRows({ m, year, showYoy, get, sum }: { m: Metric; year: number; showYoy: boolean; get: (y: number, m: number) => number | null; sum: number | null }) {
  return (
    <>
      <tr className="border-t border-line">
        <td className="td whitespace-nowrap font-medium" title={TIP[m]}>{LABEL[m]}</td>
        <td className="td text-right tabular-nums font-medium">{money(sum)}</td>
        {MONTHS.map((mm) => { const v = get(year, mm); return <td key={mm} className={`td text-right tabular-nums ${v !== null && v < 0 ? 'text-red-700' : ''}`}>{money(v)}</td>; })}
      </tr>
      {showYoy && (
        <tr className="text-ink-soft">
          <td className="td pl-4">작년대비</td><td className="td" />
          {MONTHS.map((mm) => {
            const a = get(year, mm), b = get(year - 1, mm);
            const r = a === null || !b ? null : (a - b) / Math.abs(b);
            return <td key={mm} className={`td text-right tabular-nums ${tone(r)}`}>{pct(r)}</td>;
          })}
        </tr>
      )}
    </>
  );
}

/** 올해·작년·2년 전 월별 선(한 축). 작년·2년 전은 선 모양으로도 구분한다(색만으로 가르지 않게). */
function CompareChart({ year, metric, get }: { year: number; metric: Metric; get: (y: number, m: number) => number | null }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 720, H = 220, L = 56, R = 12, T = 12, B = 26;
  const series = [
    { y: year, cls: 'stroke-brand-600', dash: undefined, w: 2.5 },
    { y: year - 1, cls: 'stroke-ink-soft', dash: '6 4', w: 1.8 },
    { y: year - 2, cls: 'stroke-ink-faint', dash: '2 3', w: 1.5 },
  ];
  const all = series.flatMap((s) => MONTHS.map((m) => get(s.y, m))).filter((v): v is number => v !== null);
  if (!all.length) return <div className="py-10 text-center text-xs text-ink-faint">기록이 없습니다.</div>;
  const lo = Math.min(0, ...all), hi = Math.max(...all);
  const raw = (hi - lo || 1) / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const unit = [1, 2, 5, 10].map((k) => k * pow).find((s) => s >= raw)!;
  const min = Math.floor(lo / unit) * unit, max = Math.ceil(hi / unit) * unit;
  const x = (i: number) => L + (i * (W - L - R)) / 11;
  const y = (v: number) => T + (1 - (v - min) / (max - min || 1)) * (H - T - B);
  const ticks: number[] = []; for (let t = min; t <= max + 1e-6; t += unit) ticks.push(t);
  return (
    <div className="relative">
      <div className="mb-1 flex gap-4 text-xs text-ink-soft">
        {series.map((s) => (
          <span key={s.y} className="flex items-center gap-1">
            <svg width="22" height="8"><line x1="0" x2="22" y1="4" y2="4" className={s.cls} strokeWidth={s.w} strokeDasharray={s.dash} /></svg>{s.y}
          </span>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label={`${LABEL[metric]} 연도 비교`} onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={L} x2={W - R} y1={y(t)} y2={y(t)} className={t === 0 ? 'stroke-ink-faint' : 'stroke-line'} strokeWidth={1} />
            <text x={L - 6} y={y(t) + 3} textAnchor="end" className="fill-ink-soft text-[10px]">{eok(t)}</text>
          </g>
        ))}
        {MONTHS.map((m, i) => <text key={m} x={x(i)} y={H - 8} textAnchor="middle" className="fill-ink-soft text-[10px]">{m}월</text>)}
        {series.map((s) => {
          const pts = MONTHS.map((m, i) => { const v = get(s.y, m); return v === null ? null : `${x(i)},${y(v)}`; });
          // 빈 달에서 선을 끊는다(이어 그리면 없는 값을 지어낸다).
          const segs: string[][] = []; let cur: string[] = [];
          for (const p of pts) { if (p) cur.push(p); else if (cur.length) { segs.push(cur); cur = []; } }
          if (cur.length) segs.push(cur);
          return segs.map((g, k) => g.length === 1
            ? <circle key={`${s.y}-${k}`} cx={g[0]!.split(',')[0]} cy={g[0]!.split(',')[1]} r={3} className={`fill-surface ${s.cls}`} strokeWidth={2} />
            : <polyline key={`${s.y}-${k}`} fill="none" className={s.cls} strokeWidth={s.w} strokeDasharray={s.dash} strokeLinejoin="round" points={g.join(' ')} />);
        })}
        {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={T} y2={H - B} className="stroke-ink-faint" strokeDasharray="3 3" />}
        {MONTHS.map((m, i) => (
          <rect key={m} x={x(i) - (W - L - R) / 22} y={T} width={(W - L - R) / 11} height={H - T - B} fill="transparent" onMouseEnter={() => setHover(i)} />
        ))}
      </svg>
      {hover !== null && (
        <div className="pointer-events-none absolute top-6 rounded border border-line bg-surface px-2 py-1 text-xs shadow"
             style={{ left: `${Math.min(78, (x(hover) / W) * 100)}%` }}>
          <div className="font-semibold">{hover + 1}월 {LABEL[metric]}</div>
          {series.map((s) => { const v = get(s.y, hover + 1); return <div key={s.y}>{s.y}: {v === null ? '—' : `${money(v)}원`}</div>; })}
        </div>
      )}
    </div>
  );
}

/** 목표 영업매출 — 시트 「목표영업매출」 표. 달성해야 할 월매출 = 남은 금액 ÷ 남은 달 수. */
function Targets({ year, n, last, cum, lastMonth, targets, canWrite, onChanged }: {
  year: number; n: number; last: number; cum: number | null; lastMonth: number | null;
  targets: { id: number; amt: string }[]; canWrite: boolean; onChanged: () => void;
}) {
  const [amt, setAmt] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const add = async () => {
    setBusy(true); setErr(null);
    try { await api.saveProfitTarget({ yr: year, amt: Number(amt) }); setAmt(''); onChanged(); }
    catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const drop = async (id: number) => {
    setBusy(true); setErr(null);
    try { await api.saveProfitTarget({ deleteId: id }); onChanged(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const left = 12 - n;
  return (
    <section className="card p-4">
      <h2 className="mb-2 text-sm font-semibold">{year}년 목표 영업매출 <span className="font-normal text-xs text-ink-soft">(통근 + 일반)</span></h2>
      {err && <div className="mb-2 text-xs text-red-700">{err}</div>}
      {targets.length === 0 ? <div className="text-xs text-ink-faint">목표가 없습니다.</div> : (
        <div className="overflow-x-auto">
          <table className="min-w-[44rem] text-sm">
            <thead><tr className="text-xs text-ink-soft">
              <th className="th text-right">목표</th><th className="th text-right">현재 달성액</th><th className="th text-right">남은 금액</th>
              <th className="th text-right">달성해야 할 월매출</th><th className="th text-right">{last ? `${last}월 매출` : '금월 매출'}</th>
              <th className="th text-right">달성률</th>{canWrite && <th className="th" />}
            </tr></thead>
            <tbody>
              {targets.map((t) => {
                const goal = Number(t.amt), done = cum ?? 0, rest = goal - done, need = left > 0 ? rest / left : null;
                return (
                  <tr key={t.id} className="border-t border-line">
                    <td className="td text-right tabular-nums">{money(goal)}</td>
                    <td className="td text-right tabular-nums">{money(done)}</td>
                    <td className="td text-right tabular-nums">{money(rest)}</td>
                    <td className="td text-right tabular-nums">{need === null ? '—' : money(Math.round(need))}
                      {need !== null && lastMonth !== null && <div className={`text-xs ${lastMonth >= need ? 'text-green-700' : 'text-red-700'}`}>{lastMonth >= need ? '달성 중' : `월 ${money(Math.round(need - lastMonth))} 부족`}</div>}
                    </td>
                    <td className="td text-right tabular-nums">{money(lastMonth)}</td>
                    <td className="td text-right tabular-nums">{goal ? `${(done / goal * 100).toFixed(1)}%` : '—'}</td>
                    {canWrite && <td className="td text-right"><button className="btn-ghost text-xs" disabled={busy} onClick={() => drop(t.id)}>지우기</button></td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {canWrite && (
        <div className="mt-3 flex items-center gap-2">
          <MoneyInput className="field w-48" placeholder="목표 금액(원)" value={amt} onChange={setAmt} aria-label="목표 금액" />
          <button className="btn" disabled={busy || !amt} onClick={add}>목표 추가</button>
        </div>
      )}
    </section>
  );
}

type Tab = 'month' | 'cust' | 'ctrend' | 'route' | 'misu' | 'idle' | 'inst' | 'direct' | 'repair' | 'minus' | 'trend';
const TABS: readonly (readonly [Tab, string])[] = [['month', '월 수익'], ['cust', '거래처별'], ['ctrend', '거래처 추세'], ['route', '노선별'], ['misu', '미수'], ['idle', '일 없는 기사'], ['inst', '할부금'], ['direct', '관리기사A 차량'], ['repair', '수리비'], ['minus', '차인 마이너스'], ['trend', '추세']];
/** 세부 탭 [id, 이름] — 화면 탭 이름·화면 찾기(App.tsx)가 쓴다. */
export const PROFIT_TABS: [string, string][] = TABS.map(([k, l]) => [k, l]);
/** 화면 권한 `profit` 이 없으면 이 탭만(10-27 way). */
export const PROFIT_OPEN_TAB: Tab = 'ctrend';

/**
 * 화면 — 「월 수익」(그 달 계산·손입력·굳히기) · 「거래처별」(거래처·담당자 수익과 전월 증감) · 「추세」(굳힌 기록) 등.
 * 🔴 메뉴는 전 직원에게 보인다 — 「거래처 추세」만 전원(10-27 way), 나머지 탭은 화면 권한 `profit`(서버도 같은 게이트).
 * 세부 탭마다 **따로 화면 탭**(주소 `/profit?tab=…` · 기준정보·접수·배차와 같은 방식, 10-27 way) — 오가도 고른 달·조건 그대로.
 */
export default function Profit() {
  const { me } = useMe();
  const full = canScreen(me, 'profit', 'read');
  const { search } = useLocation();
  const navigate = useNavigate();
  const pageTabs = usePageTabs();
  const urlTab = urlParam(search, 'tab');
  const tab: Tab = full && TABS.some(([k]) => k === urlTab) ? (urlTab as Tab) : PROFIT_OPEN_TAB;
  // 상단 메뉴로 「경영 분석」을 다시 열 때 마지막에 본 세부 탭부터(App.tsx Pages).
  const [, setLastTab] = useSticky<Tab>('profit.tab', 'month');
  const pageActive = usePageActive();
  useEffect(() => { if (pageActive && full) setLastTab(tab); }, [pageActive, full, tab, setLastTab]);
  const setTab = (id: Tab) => {
    if (id === tab) return;
    const open = pageTabs.find((t) => t.key === profitKey(id));
    navigate(open ? `${open.loc.pathname}${open.loc.search}` : `${PROFIT_PATH}?tab=${id}`);
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-base font-semibold">경영 분석</h1>
        {full ? (
          <div className="flex flex-wrap gap-1">
            {TABS.map(([k, l]) => (
              <button key={k} className={`${tab === k ? 'btn-primary' : 'btn'} px-2.5 py-1 text-sm`} onClick={() => setTab(k)}>{l}</button>
            ))}
          </div>
        ) : <span className="text-sm text-ink-soft">› 거래처 추세</span>}
      </div>
      {tab === 'month' ? <MonthView /> : tab === 'cust' ? <ProfitCust /> : tab === 'ctrend' ? <ProfitCustTrend /> : tab === 'route' ? <ProfitRoute /> : tab === 'misu' ? <ProfitMisu /> : tab === 'idle' ? <ProfitIdle /> : tab === 'inst' ? <ProfitInst /> : tab === 'direct' ? <ProfitDirect /> : tab === 'repair' ? <ProfitRepair /> : tab === 'minus' ? <ProfitMinus /> : <TrendView />}
    </div>
  );
}

const MANUAL: { key: ProfitManualItem; label: string; side: '수입' | '지출' }[] = [
  { key: 'rent', label: '사옥 임대료', side: '수입' }, { key: 'mgmt', label: '사옥 관리비', side: '수입' },
  { key: 'land', label: '파주토지 임대료', side: '수입' },
  { key: 'card_etc', label: '법인카드(*) 해외 사용 원화 환산(자동 원화 합에 더함)', side: '지출' },
];
/** 자동으로 가져오는 칸 — 손입력 창에 없다(서버 `profitAuto.ts`). */
const AUTO: { key: 'card_staff' | 'card_etc'; label: string }[] = [
  { key: 'card_staff', label: '법인카드(관리직+직영)' }, { key: 'card_etc', label: '법인카드(*) · 임원카드 원화' },
];
const CARD_ROWS: Metric[] = ['rev', 'exp', 'profit', 'real_sales', 'commute_sales', 'general_sales', 'commute_profit', 'general_profit', 'personal', 'direct_cost', 'fin_add'];
const prevMonOf = (m: string) => { const [y, mm] = m.split('-').map(Number) as [number, number]; return mm === 1 ? `${y - 1}-12` : `${y}-${String(mm - 1).padStart(2, '0')}`; };

function MonthView() {
  const { me } = useMe();
  const canWrite = canScreen(me, 'profit', 'write');
  // 굳힌 값(추세에 쓰는 기록)과 다시 계산한 값의 차이는 슈퍼관리자만 본다(10-27 way) — 다른 조회자에겐 지금 값 하나만.
  const showSaved = me?.role === 'admin';
  const [trend, setTrend] = useState<ProfitMonthRow[]>([]);
  const [latest, setLatest] = useState<string | null>(null);
  const [mon, setMon] = useState('');
  const [data, setData] = useState<ProfitMonthResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [manualOpen, setManualOpen] = useState(false);

  useEffect(() => {
    api.listProfitTrend({}).then((r) => {
      setTrend(r.rows); setLatest(r.latestSalaryMon);
      setMon((m) => m || r.latestSalaryMon || r.rows.at(-1)?.mon || '');
    }).catch((e) => setError(errMsg(e)));
  }, [nonce]);
  useEffect(() => {
    if (!mon) return;
    let cancelled = false;
    setLoading(true); setError(null); setData(null);
    api.getProfitMonth({ mon }).then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(errMsg(e)); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mon, nonce]);

  const months = useMemo(() => {
    const set = new Set(trend.map((r) => r.mon));
    if (latest) set.add(latest);
    return [...set].sort().reverse();
  }, [trend, latest]);
  const prevRow = trend.find((r) => r.mon === prevMonOf(mon));
  const metrics: Record<string, number> | null = data?.live?.metrics
    ?? (data?.row ? Object.fromEntries(CARD_ROWS.map((m) => [m, val(data.row!, m) ?? NaN])) : null);
  const lines: ProfitLine[] = data?.live?.lines ?? data?.row?.detail?.lines ?? [];

  const save = async () => {
    if (!data || data.source === 'sheet') return;
    const msg = data.saved
      ? `${mon} 을 지금 시각 기준으로 다시 굳힐까요?`
      : `${mon} 을 지금 시각 기준으로 굳힐까요?\n(그 뒤 고쳐지는 정산·출납은 이 달 기록을 바꾸지 않습니다)`;
    if (!window.confirm(msg)) return;
    setBusy('save'); setError(null);
    try { await api.saveProfitMonth({ mon }); setNonce((x) => x + 1); } catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select className="field w-auto" value={mon} onChange={(e) => setMon(e.target.value)} aria-label="정산월">
          {months.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        {data && (
          <span className={`rounded px-2 py-0.5 text-xs ${data.source === 'live' ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-700'}`}>
            {data.source === 'sheet' ? '시트에서 옮긴 기록' : data.source === 'web' ? `굳힌 기록 · ${data.row?.cut_at?.slice(0, 16)} 기준` : '계산 미리보기 — 아직 안 굳힘'}
          </span>
        )}
        <div className="ml-auto flex gap-2">
          {canWrite && data && data.source !== 'sheet' && <button className="btn" onClick={() => setManualOpen(true)}>손입력 칸</button>}
          {canWrite && data && data.source !== 'sheet' && (
            <button className="btn-primary" disabled={!!busy} onClick={save}>{busy === 'save' ? '굳히는 중…' : data.saved ? '다시 굳히기' : '이 달 기록 굳히기'}</button>
          )}
        </div>
      </div>
      {error && <ErrorBox message={error} />}
      {loading && <div className="py-8 text-center text-sm text-ink-soft">계산하는 중…</div>}
      {showSaved && data?.source === 'web' && data.live && (
        <div className="card border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          아래 숫자는 <b>지금 다시 계산한 값</b>입니다. 굳힌 기록(추세에 쓰는 값)과 다르면 그 사이 정산·출납이 고쳐진 것입니다 — 반영하려면 「다시 굳히기」.
        </div>
      )}
      {metrics && (
        <section className="card p-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {CARD_ROWS.map((m) => {
              const v = metrics[m] ?? NaN;
              const pv = val(prevRow, m);
              const d = Number.isNaN(v) || pv === null ? null : v - pv;
              const savedV = showSaved && data?.source === 'web' && data.row ? val(data.row, m) : null;
              return (
                <div key={m} className="rounded border border-line p-3" title={TIP[m]}>
                  <div className="text-xs text-ink-soft">{LABEL[m]}</div>
                  <div className={`text-base font-semibold tabular-nums ${v < 0 ? 'text-red-700' : ''}`}>{Number.isNaN(v) ? '—' : money(v)}</div>
                  {m !== 'fin_add' && d !== null && <div className={`text-xs ${tone(d)}`}>전월대비 {d >= 0 ? '+' : ''}{money(d)}</div>}
                  {m === 'fin_add' && data?.live && !data.live.prevDetail && <div className="text-xs text-ink-faint">지난달 계정 내역이 없어 비교 못 함</div>}
                  {savedV !== null && data?.live && Math.round(savedV) !== Math.round(v) && <div className="text-xs text-amber-700">굳힌 값 {money(savedV)}</div>}
                </div>
              );
            })}
          </div>
          {data?.live && <div className="mt-2 text-xs text-ink-soft">직영차량 비용 배분(통근 접수 몫) {money(data.live.directAlloc)} — 통근수익에서 뺍니다(총수익과는 무관).</div>}
          {data?.live && <AutoNotes live={data.live} />}
          {data?.source === 'sheet' && <div className="mt-2 text-xs text-ink-soft">시트에서 옮긴 달은 합계만 있고 계정 내역이 없습니다.</div>}
        </section>
      )}
      {lines.length > 0 && <LineTables lines={lines} />}
      {manualOpen && data && data.source !== 'sheet' && (
        <Modal width="max-w-md">
          <ManualForm mon={mon} cur={manualOnly(data.live?.manual ?? data.row?.detail?.manual ?? null, data.live?.auto?.card_etc)}
                      onClose={() => setManualOpen(false)} onSaved={() => { setManualOpen(false); setNonce((x) => x + 1); }} />
        </Modal>
      )}
    </div>
  );
}

/** 손입력 창에 넣을 값 — 법인카드(*) 는 저장된 합에서 자동 원화분을 뺀 「해외 환산」 손입력분만. */
function manualOnly(m: Record<ProfitManualItem, number> | null, etc: ProfitAutoVal | undefined): Record<ProfitManualItem, number> | null {
  if (!m) return null;
  return { ...m, card_etc: etc?.ok ? m.card_etc - etc.amt : m.card_etc };
}

/** 자동 칸(법인카드 2 · 부가세상계)이 어디서 얼마로 왔나 — 비었거나 못 가져오면 노랑·빨강. */
function AutoNotes({ live }: { live: ProfitLive }) {
  return (
    <div className="mt-3 space-y-1 border-t border-line pt-2 text-xs">
      {AUTO.map((a) => {
        const v = live.auto?.[a.key];
        const tone = !v || !v.ok ? 'text-red-700' : v.blank ? 'text-amber-700' : 'text-ink-soft';
        return (
          <div key={a.key} className={tone}>
            <b>{a.label}</b> {v?.ok && !v.blank ? money(v.amt) : '—'} · 자동({v?.source ?? '가져오지 못함'}) — {v?.note ?? ''}
            {v?.blank && ' 이대로는 굳힐 수 없습니다.'}
          </div>
        );
      })}
      {live.auto?.card_etc.ok && !live.auto.card_etc.blank && live.manual.card_etc !== live.auto.card_etc.amt && (
        <div className="text-ink-soft"><b>법인카드(*) 해외 환산(손입력)</b> {money(live.manual.card_etc - live.auto.card_etc.amt)} → 법인카드(*) 합 {money(live.manual.card_etc)}</div>
      )}
      <div className="text-ink-soft">
        <b>부가세상계</b> −{money(live.vatOffset)} · 자동(출납 「세금납부」 중 적요 「부가세 납부」 줄) — 부가세는 비용에서 뺍니다.
      </div>
    </div>
  );
}

function LineTables({ lines }: { lines: ProfitLine[] }) {
  const sides: ['in' | 'out', string][] = [['in', '수입'], ['out', '지출']];
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {sides.map(([side, title]) => {
        const xs = lines.filter((l) => l.side === side);
        const groups = [...new Set(xs.map((l) => l.group))];
        const total = xs.reduce((a, l) => a + l.amt, 0);
        return (
          <section key={side} className="card p-4">
            <h2 className="mb-2 text-sm font-semibold">{title} <span className="font-normal text-ink-soft">{money(total)}</span></h2>
            <div className="max-h-[calc(100vh-14rem)] overflow-auto">
              <table className="w-full text-xs">
                <tbody>
                  {groups.map((g) => <GroupRows key={g} group={g} rows={xs.filter((l) => l.group === g)} />)}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}

function GroupRows({ group, rows }: { group: string; rows: ProfitLine[] }) {
  const [open, setOpen] = useState(rows.length <= 3);
  const sum = rows.reduce((a, l) => a + l.amt, 0);
  return (
    <>
      <tr className="cursor-pointer border-t border-line" onClick={() => setOpen((o) => !o)}>
        <td className="td font-medium">{open ? '▾' : '▸'} {group} <span className="text-ink-faint">({rows.length})</span></td>
        <td className="td text-right font-medium tabular-nums">{money(sum)}</td>
      </tr>
      {open && rows.map((l, i) => (
        <tr key={i}><td className="td pl-6">{l.account}</td><td className={`td text-right tabular-nums ${l.amt === 0 ? 'text-ink-faint' : ''}`}>{money(l.amt)}</td></tr>
      ))}
    </>
  );
}

function ManualForm({ mon, cur, onClose, onSaved }: {
  mon: string; cur: Record<ProfitManualItem, number> | null; onClose: () => void; onSaved: () => void;
}) {
  const [vals, setVals] = useState<Record<ProfitManualItem, string>>(() =>
    Object.fromEntries(MANUAL.map((m) => [m.key, cur?.[m.key] ? String(cur[m.key]) : ''])) as Record<ProfitManualItem, string>);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const fromPrev = async () => {
    setErr(null); setHint(null);
    try {
      const r = await api.getProfitMonth({ mon: prevMonOf(mon) });
      const pm = r.live?.manual ?? r.row?.detail?.manual;
      if (!pm) { setHint('지난달 손입력 값이 없습니다.'); return; }
      // 해외 환산은 달마다 다르다 — 지난달 값을 옮기지 않는다.
      setVals((cur) => ({ ...(Object.fromEntries(MANUAL.map((m) => [m.key, pm[m.key] ? String(pm[m.key]) : ''])) as Record<ProfitManualItem, string>), card_etc: cur.card_etc }));
    } catch (e) { setErr(errMsg(e)); }
  };
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api.saveProfitManual({ mon, values: Object.fromEntries(MANUAL.map((m) => [m.key, Number(vals[m.key] || 0)])) as Record<ProfitManualItem, number> });
      onSaved();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card w-[28rem] max-w-full space-y-3 p-5">
      <h2 className="text-sm font-semibold">{mon} 손입력 칸</h2>
      <p className="text-xs text-ink-soft">시트 ➗수익분석에 손으로 넣던 값입니다. 원장·출납일보에 없는 금액만 넣습니다. 법인카드(관리직+직영)는 자동(DYOPS)이고, 법인카드(*)는 임원카드 시트 원화 합이 자동이라 여기엔 해외(USD) 사용을 원화로 바꾼 금액만 넣습니다.</p>
      {MANUAL.map((m) => (
        <div key={m.key} className="text-xs">
          <div className="text-ink-soft">{m.side} · {m.label}</div>
          <MoneyInput className="field mt-1" value={vals[m.key]} onChange={(v) => setVals((s) => ({ ...s, [m.key]: v }))} aria-label={m.label} />
        </div>
      ))}
      {hint && <div className="text-xs text-ink-soft">{hint}</div>}
      {err && <div className="text-xs text-red-700">{err}</div>}
      <div className="flex justify-between gap-2">
        <button className="btn-ghost text-xs" onClick={fromPrev}>지난달 값 가져오기</button>
        <div className="flex gap-2">
          <button className="btn" onClick={onClose}>취소</button>
          <button className="btn-primary" disabled={busy} onClick={submit}>{busy ? '저장 중…' : '저장'}</button>
        </div>
      </div>
    </div>
  );
}
